import { useSociete } from "../contexts/SocieteContext";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { Building2, Layers, X } from "lucide-react";

/**
 * SocieteScopeBanner — bandeau visible sur les pages "isolées" qui informe
 * l'utilisateur que les données sont filtrées par société, avec un bouton
 * "Vue groupe" (superadmin only) qui efface temporairement le X-Societe-Id.
 *
 * Pose-le en haut de chaque page d'isolation. Aucun effet si :
 *   - aucune société active (vue groupe en cours)
 *   - aucun useSociete context (cas rare)
 */
export function SocieteScopeBanner({ className = "" }) {
  const { user } = useAuth();
  const { currentSociete } = useSociete();
  const location = useLocation();

  const isSuperadmin = ["superadmin", "admin"].includes(user?.role) && !user?.tenant_id;
  const sid = typeof window !== "undefined" ? localStorage.getItem("blitzz_societe_id") : null;
  // Panel Super Admin : le contexte société n'a pas de sens (gestion des tenants)
  const inSuperPanel = ["/admin/master", "/admin/preflight", "/admin/observability", "/admin/security"]
    .some(p => location.pathname === p || location.pathname.startsWith(p + "/"));

  const switchToGroup = () => {
    if (typeof window === "undefined") return;
    // Sentinel value tells SocieteContext.loadSocietes NOT to re-apply the
    // default société. Cleared by switchSociete when user picks one again.
    localStorage.setItem("blitzz_societe_id", "__group__");
    window.location.reload();
  };

  if (!sid || sid === "__group__" || !currentSociete || inSuperPanel) return null;

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${className}`}
      style={{ background: "rgba(247,201,53,0.05)", borderColor: "rgba(247,201,53,0.25)" }}
      data-testid="societe-scope-banner">
      {currentSociete.logo ? (
        <img src={currentSociete.logo} alt="" className="w-4 h-4 rounded object-contain flex-shrink-0" />
      ) : (
        <Building2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: currentSociete.color || "#f7c935" }} />
      )}
      <span className="flex-1" style={{ color: "var(--text-primary)" }}>
        Donnees filtrees sur <strong style={{ color: "#f7c935" }}>{currentSociete.name}</strong>
        {currentSociete.siren && <span className="text-slate-500 ml-1.5">- SIREN {currentSociete.siren}</span>}
      </span>
      {isSuperadmin && (
        <button onClick={switchToGroup}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border border-white/10 hover:border-[#f7c935]/40 hover:text-[#f7c935] transition-colors"
          style={{ color: "var(--text-muted)" }}
          data-testid="societe-scope-group-view-btn"
          title="Voir toutes les societes (cross-tenant)"
        >
          <Layers className="w-3 h-3" /> Vue groupe
        </button>
      )}
      <button onClick={switchToGroup}
        className="p-1 rounded hover:bg-white/5 transition-colors"
        style={{ color: "var(--text-muted)" }}
        data-testid="societe-scope-clear-btn"
        title="Lever le filtre societe"
        aria-label="Lever le filtre societe"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

export default SocieteScopeBanner;
